import { useState, useRef, useEffect } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/previewPartnerPage.module.scss';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatStatus } from '../helpers/functions';
import tableStyles from '../css/table.module.scss';
import Pagination from '../components/pagination';
import { createPortal } from 'react-dom';
import CustomDropdown from '../components/customDropdown';
import CustomSearchInput from '../components/customSearchInput';
import { useClickAway } from 'react-use';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

export interface OrderHistoryItem {
  orderId?: string;
  orderDate: Date;
  planName: string;
  planPrice: number;
  singleFlight?: boolean;
  buyerName?: string;
  buyerEmail: string;
  referralCut: number;
  quantity?: number;
  expirationDate?: Date | null;
}

interface OrganizationMemberInterface {
  dateJoined: Date;
  userId: {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    password?: string;
    isVerified: boolean;
    verificationToken?: string;
    verificationTokenExpiration?: Date;
    resetToken?: string;
    resetTokenExpiration?: Date;
    isBlocked: boolean;
    googleId?: string;
    avatarUrl?: string;
    organizationRole: 'owner' | 'member';
    organizationId?: string;
    referralLink?: string;
    waitingForConfirmation?: boolean;
    referralLinkVisits?: number;
    orderHistory: OrderHistoryItem[];
  };
}
interface OrganizationInterface {
  _id: string;
  companyName: string;
  companyAdmin: string;
  totalPlansSold: number;
  totalEarnings: number;
  members: OrganizationMemberInterface[];
  createdAt: Date;
  pagination: {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
    totalMembers: number;
  };
}

const PreviewOrganizationPage = () => {
  const [fetchedData, setFetchedData] = useState<OrganizationInterface>();
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const params = useParams();
  const { id } = params;
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isActionOpen, setIsActionOpen] = useState<number | string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [sortByEarnings, setSortByEarnings] = useState<{ value: string; label: string }>({ value: 'desc', label: t('previewOrganization.labels.descending') });
  const [filterSortByEarningsOpen, setFilterSortByEarningsOpen] = useState<boolean>(false);
  const filterSortByEarningsDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleActionClick = (id: number | string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  useClickAway(filterSortByEarningsDropRef, () => {
    setFilterSortByEarningsOpen(false);
  });

  const filterSortByEarningsOptions = [
    { value: 'asc', label: t('previewOrganization.labels.ascending') },
    { value: 'desc', label: t('previewOrganization.labels.descending') },
  ];

  const handleSortByEarningsChange = (item: { value: string; label: string }) => {
    setSortByEarnings(item);
    setFilterSortByEarningsOpen(false);
  };

  const fetchData = async () => {
    setLoading(true);
    axios
      .get(`${EnvPaths.SERVER_URL}/admin/preview-partner-organization/${id}?page=${currentPage}&sortByEarnings=${sortByEarnings.value}&q=${searchTerm}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setFetchedData({
          ...res.data?.foundOrganization,
          totalEarnings: res.data?.totalEarnings,
          totalPlansSold: res.data?.totalPlansSold,
          pagination: res.data?.pagination,
          companyAdmin: res.data?.companyAdmin,
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchTerm, sortByEarnings]);

  useEffect(() => {
    setSortByEarnings((prev) => ({ ...prev, label: t('previewOrganization.labels.descending') }));
  }, [t, i18next]);

  const handleCopyToClipboard = async (link: string) => {
    try {
      const referralLink = EnvPaths.SITE_URL + '/explore-plans?referral=' + link;
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success(t('previewOrganization.handleCopyToClipboard.success'));
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('previewOrganization.handleCopyToClipboard.error'));
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      await axios
        .get(`${EnvPaths.SERVER_URL}/admin/deactivate-user/${userId}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error('Deactivation failed:', error);
        });
    } catch (error) {
      console.error('Deactivation failed:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm(t('previewOrganization.handleDeleteUser'))) return;
    try {
      await axios
        .delete(`${EnvPaths.SERVER_URL}/admin/delete-user/${userId}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error('Deletion failed:', error);
        });
    } catch (error) {
      console.error('Deletion failed:', error);
    }
  };

  const handleCSVExport = async () => {
    try {
      const response = await fetch(`${EnvPaths.SERVER_URL}/admin/export-partner-members/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `organization_analytics.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <main className={globalPageStyle.pageContainer}>
      <div className={styles.pageContent}>
        {/*header*/}
        <div className={styles.header}>
          <Link to="/dashboard/partner-program">
            <div className={styles.goBackButton}>
              <img src="/assets/images/back-icon.svg" alt="Go Back" />
            </div>
          </Link>
          <p>{t('previewOrganization.goBack')}</p>
        </div>
        {/*header*/}
        {/*body*/}
        <div className={globalPageStyle.pageContent}>
          {/*container flex gap 24*/}
          {loading ? (
            <div className="loader" />
          ) : (
            <div className={styles.overviewContainer}>
              {/*topbar - name, email*/}
              <div className={styles.overviewHeader}>
                <div className={styles.overviewHeaderLeft}>
                  <div className={styles.overviewItem}>
                    <p>{t('previewOrganization.name')}</p>
                    <p className={styles.overviewItemValue}>{fetchedData?.companyName ?? 'N/A'}</p>
                  </div>
                  <div className={styles.overviewItem}>
                    <p>{t('previewOrganization.organizationId')}</p>
                    <p className={styles.overviewItemValue}>{fetchedData?._id ?? 'N/A'}</p>
                  </div>
                  <div className={styles.overviewItem}>
                    <p>{t('previewOrganization.date')}</p>
                    <p className={styles.overviewItemValue}>{fetchedData?.createdAt ? new Date(fetchedData.createdAt).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div className={styles.overviewItem}>
                    <p>{t('previewOrganization.status')}</p>
                    <div className={styles.overviewItemValue}>{formatStatus(t, 'active')}</div>
                  </div>
                  <div className={styles.overviewItem}>
                    <p>{t('users.labels.admin')}</p>
                    <div className={styles.overviewItemValue}>{fetchedData?.companyAdmin ?? 'N/A'}</div>
                  </div>
                </div>
              </div>
              {/*topbar - name, email*/}
              {/*statistics*/}
              <div className={globalPageStyle.statistics}>
                {/*Total Earnings*/}
                <div className={globalPageStyle.statisticsItem}>
                  <p>{t('previewOrganization.totalEarnings')}</p>
                  <div className={globalPageStyle.statisticsItemBottom}>
                    <div className={globalPageStyle.statisticsItemBottomLeft}>
                      <p className={globalPageStyle.largeP}>€{fetchedData?.totalEarnings?.toFixed(2) ?? 'N/A'}</p>
                    </div>
                  </div>
                </div>
                {/*Total Earnings*/}
                {/*Total Plans Sold*/}
                <div className={globalPageStyle.statisticsItem}>
                  <p>{t('previewOrganization.totalPlansSold')}</p>
                  <div className={globalPageStyle.statisticsItemBottom}>
                    <div className={globalPageStyle.statisticsItemBottomLeft}>
                      <p className={globalPageStyle.largeP}>{fetchedData?.totalPlansSold ?? 'N/A'}</p>
                    </div>
                  </div>
                </div>
                {/*Total Plans Sold*/}
                {/*Number of Active Members*/}
                <div className={globalPageStyle.statisticsItem}>
                  <p>{t('previewOrganization.numberOdActiveMembers')}</p>
                  <div className={globalPageStyle.statisticsItemBottom}>
                    <div className={globalPageStyle.statisticsItemBottomLeft}>
                      <p className={globalPageStyle.largeP}>{fetchedData?.pagination?.totalMembers ?? 'N/A'}</p>
                    </div>
                  </div>
                </div>
                {/*Number of Active Members*/}
              </div>
              {/*statistics*/}
              {/*table with title and filters*/}
              <div className={styles.tableContainer}>
                <p className={styles.tableTitle}>Members</p>
                {/*filters*/}
                {/*filters*/}
                <div className={styles.tableHeader}>
                  <div className={styles.tableHeaderLeft}>
                    <div className={styles.tableFlexRow}>
                      <CustomSearchInput onSearch={handleSearch} icon="/assets/images/search-icon.svg" placeholder={t('previewOrganization.search')} />
                      {/*filter*/}
                      <CustomDropdown
                        ref={filterSortByEarningsDropRef}
                        isOpen={filterSortByEarningsOpen}
                        onToggle={() => setFilterSortByEarningsOpen(!filterSortByEarningsOpen)}
                        options={filterSortByEarningsOptions}
                        value={sortByEarnings}
                        onChange={handleSortByEarningsChange}
                        width="255px"
                        label={t('previewOrganization.sortByEarnings')}
                      />
                      {/*filter*/}
                    </div>
                  </div>
                  <button type="button" className="primaryButton inverse" onClick={handleCSVExport}>
                    {t('previewOrganization.exportCsv')}{' '}
                  </button>
                </div>
                {/*filters*/}
                {/*filters*/}
                {/*table*/}
                <div className={tableStyles.tableContainer}>
                  <table className={`${tableStyles.table}`}>
                    <thead className={tableStyles.tableHeader}>
                      <tr>
                        <th>{t('previewOrganization.teamMember')}</th>
                        <th>{t('previewOrganization.referralLink')}</th>
                        <th>{t('previewOrganization.planPurchases')}</th>
                        <th>{t('previewOrganization.earnings')}</th>
                        <th className={tableStyles.actionColumn}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchedData?.members?.map((item: OrganizationMemberInterface, index: number) => {
                        return (
                          <tr key={item.userId._id}>
                            <td>{item.userId.organizationRole === 'owner' ? 'Admin' : item.userId.firstName + ' ' + item.userId.lastName}</td>
                            <td>{item.userId.referralLink}</td>
                            <td>{item.userId.orderHistory?.length ?? 'N/A'}</td>
                            <td>€{item.userId.orderHistory?.reduce((total, order) => total + order.referralCut, 0) ?? 'N/A'}</td>
                            <td className={tableStyles.actionColumn}>
                              <div className={tableStyles.actionShape} ref={buttonRef}>
                                <img
                                  src="/assets/images/dots.svg"
                                  alt="dots"
                                  onClick={(e) => {
                                    const buttonElement = e.currentTarget as HTMLDivElement;
                                    handleActionClick(item.userId._id, buttonElement);
                                  }}
                                />
                                {isActionOpen === item.userId._id &&
                                  typeof window !== 'undefined' &&
                                  createPortal(
                                    <div
                                      className={`${tableStyles.actionShapeDropdown}`}
                                      style={{
                                        top: `${dropdownPosition.top}px`,
                                        left: `${dropdownPosition.left}px`,
                                      }}
                                    >
                                      <div>
                                        <Link to={`/dashboard/partner-program/preview-partner/${item.userId._id}`} className={tableStyles.actionShapeDropdownItem}>
                                          {t('previewOrganization.viewPartner')}
                                        </Link>
                                      </div>
                                      {item.userId.referralLink && item.userId.referralLink !== undefined && (
                                        <button onClick={() => handleCopyToClipboard(item.userId.referralLink ?? '')} className={tableStyles.actionShapeDropdownItem} style={{ border: 'none', backgroundColor: 'transparent' }}>
                                          {t('previewOrganization.copyReferralLink')}
                                        </button>
                                      )}
                                      <div className={tableStyles.toggleContainer}>
                                        <p>{t('previewOrganization.changeStatus')}</p>
                                        <label className={tableStyles.toggleSwitch}>
                                          <input type="checkbox" checked={!item.userId.isBlocked} onChange={() => handleDeactivateUser(item.userId._id)} className={tableStyles.toggleInput} />
                                          <span className={tableStyles.toggleSlider}></span>
                                        </label>
                                      </div>
                                      {item.userId.organizationRole === 'member' && (
                                        <button onClick={() => handleDeleteUser(item.userId._id)} className={`${tableStyles.actionShapeDropdownItem} ${tableStyles.redItem}`} style={{ border: 'none', backgroundColor: 'transparent' }}>
                                          {t('previewOrganization.delete')}
                                        </button>
                                      )}
                                    </div>,
                                    document.body
                                  )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/*table*/}
                {/*pagination*/}
                <Pagination
                  onPrevious={() => {
                    setCurrentPage((prev) => prev - 1);
                  }}
                  onNext={() => {
                    setCurrentPage((prev) => prev + 1);
                  }}
                  currentPage={currentPage}
                  totalPages={fetchedData?.pagination?.totalPages ?? 1}
                />
                {/*pagination*/}
              </div>
              {/*table with title and filters*/}
            </div>
          )}
          {/*container flex gap 24*/}
        </div>
        {/*body*/}
      </div>
      <div style={{ height: '100px' }}></div>
    </main>
  );
};
export default PreviewOrganizationPage;
